import { createSelector } from '@reduxjs/toolkit';
import get from 'lodash/get';
import pick from 'lodash/pick';
import find from 'lodash/find';
import compact from 'lodash/compact';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';

import { expertAvailability, OPEN_ENDED_SESSION } from 'constants/common';
import { getPackageCostBounds, getSessionCostBounds } from 'utils/session-pack';

const expertProfileState = (state) => get(state, 'expertProfile', {});

const transformUserAccountCredit = (credit) => {
  const { name, source, balance, expired, unavailable, id, remainingAmount } = credit;

  return {
    label: `Source ${source} - ${name} (${balance})${expired ? ' - Expired' : ''}${
      unavailable ? ' - Not Available' : ''
    }`,
    disabled: expired || unavailable,
    walletId: id,
    remainingAmount,
    balance,
  };
};

const transformSession = (session = {}) => {
  const {
    id,
    limit = 0,
    slotsTaken = 0,
    waitlistSize = 0,
    waitlistSlotsTaken = 0,
    availableForWaitlist,
    enabledWaitlist = false,
    durationInMins,
  } = session;
  const availableSlots = limit - slotsTaken;

  return {
    ...session,
    callTypeId: id,
    fixedLength: isNumber(id),
    minimumTimeInMinutes: durationInMins,
    availableSlots,
    availableWaitlistSlots: waitlistSize - waitlistSlotsTaken,
    visibleWaitlist: availableSlots === 0 && enabledWaitlist,
    disabledSchedule: limit > 0 && availableSlots === 0 && !availableForWaitlist,
    ...getSessionCostBounds(session),
  };
};

export const profileSidebarSelector = createSelector(expertProfileState, (expertProfile) =>
  pick(expertProfile, [
    'avatarUrls',
    'areasOfExpertise',
    'charity',
    'startingPrice',
    'years',
    'knownFor',
    'links',
    'baseCostPerMin',
    'hideCostFields',
    'guideCostDisplay',
  ]),
);

export const profileInfoSelector = createSelector(expertProfileState, (expertProfile) => {
  const accountCredits = get(expertProfile, 'schedulingData.accountCredits', []);
  const defaultCredit = { label: 'Select a currency type...', disabled: false };

  const schedulingData = {
    availability: expertAvailability.FLEXIBLE,
    daysOfWeekDisabled: [],
    flexibleTimeSlots: [],
    availableTimeSlots: [],
    ...get(expertProfile, 'schedulingData', {}),
    accountCredits: [defaultCredit, ...accountCredits.map(transformUserAccountCredit)],
  };

  return {
    ...pick(expertProfile, [
      'id',
      'name',
      'bio',
      'location',
      'links',
      'headline',
      'avatarUrls',
      'coverImageUrl',
      'baseCostPerMin',
      'openEndedConversation',
      'videoConversation',
      'minimumTimeInMinutes',
      'verifiedLabel',
      'timezone',
      'acceptingGiftCard',
      'status',
      'inPersonOpenEndedConversation',
      'locationOpenEndedConversation',
      'reducedSearchEngineVisibility',
      'expertBadges',
      'requiredPasscodeOpenEndedConversation',
      'customColors',
      'memberFundraiserIds',
      'openEndedConversationCharityId',
      'openEndedConversationCharityName',
      'charityPercentage',
      'charityMessageVisible',
    ]),
    isAllowedVideoconference: expertProfile.videoconference && expertProfile.siteVideoconference,
    isAllowedInPersonMeeting: expertProfile.inPersonMeeting && expertProfile.siteInPersonMeeting,
    schedulingData,
    unavailable: expertProfile.phoneStatus !== 'verified',
  };
});

export const profileStoreTabSelector = createSelector(expertProfileState, (expertProfile) =>
  get(expertProfile, 'giftCardAppearances', []),
);

export const profileSessionsTabSelector = createSelector(expertProfileState, (expertProfile) =>
  get(expertProfile, 'sessions', []).map(transformSession),
);

export const profilePackagesTabSelector = createSelector(expertProfileState, (expertProfile) =>
  get(expertProfile, 'packages', []),
);

export const expertProfileSessionSelector = createSelector(
  expertProfileState,
  (_, sessionId) => sessionId,
  ({ sessions = [] }, sessionId) => ({
    ...OPEN_ENDED_SESSION,
    ...transformSession(find(sessions, ['id', sessionId])),
  }),
);

export const inPersonMeetingLocationsForSchedulingSelector = createSelector(
  expertProfileState,
  ({ inPersonMeetingLocationsForScheduling }) => inPersonMeetingLocationsForScheduling || [],
);

export const isEmptyInPersonMeetingLocationsForSchedulingSelector = createSelector(
  expertProfileState,
  ({ inPersonMeetingLocationsForScheduling }) => isEmpty(inPersonMeetingLocationsForScheduling),
);

export const packageForSchedulingSelector = createSelector(
  expertProfileState,
  (_, packageId) => packageId,
  ({ packages = [], sessions = [] }, packageId) => {
    const pack = find(packages, ['id', packageId]) || {};
    const callTypes = compact(map(get(pack, 'callTypes', []), ({ id }) => find(sessions, ['id', id])));

    return {
      ...pack,
      callTypes,
      ...getPackageCostBounds({ ...pack, callTypes }),
    };
  },
);

export const expertVolunteeringsSelector = createSelector(
  (state) => get(state, 'expertVolunteerings', []),
  (expertVolunteerings) => expertVolunteerings,
);
